import { useQuery } from "@tanstack/react-query";
import api from "@/services/api";
import { getInvoiceAttachments } from "./attachmentApi";
import type {
  InvoiceListParams,
  InvoiceListResponse,
  InvoiceSummaryParams,
  InvoiceSummaryResponse,
  V2InvoiceDetailResponse,
  InvoiceStatusResponse,
  } from "../types/invoice";
import { invoiceKeys, INVOICE_BASE_URL } from "./queryKeys";
import { toQueryString } from "@/helpers/queryString";

// --- getInvoiceList ---

export async function getInvoiceList(params: InvoiceListParams): Promise<InvoiceListResponse> {
  const qs = toQueryString(params);
  return api.get(`${INVOICE_BASE_URL}?${qs ? `${qs}&` : ""}include=credit_notes`);
}

export function invoiceListQuery(params: InvoiceListParams) {
  return {
    queryKey: invoiceKeys.list(params),
    queryFn: () => getInvoiceList(params),
    keepPreviousData: true,
  };
}

export function useInvoiceList(params: InvoiceListParams) {
  return useQuery(invoiceListQuery(params));
}

// --- getInvoiceSummary ---

export async function getInvoiceSummary(params?: InvoiceSummaryParams): Promise<InvoiceSummaryResponse> {
  const qs = toQueryString(params ?? {});
  return api.get(`${INVOICE_BASE_URL}/summary${qs ? `?${qs}` : ""}`);
}

export function invoiceSummaryQuery(params?: InvoiceSummaryParams) {
  return {
    queryKey: invoiceKeys.summary(params),
    queryFn: () => getInvoiceSummary(params),
    // Params change on every search/filter; keep the old counts on screen instead of
    // flashing the sidebar and pagination to 0 while the new summary loads.
    keepPreviousData: true,
  };
}

export function useInvoiceSummary(params?: InvoiceSummaryParams, options?: { enabled?: boolean }) {
  return useQuery({ ...invoiceSummaryQuery(params), ...options });
}

// --- getInvoiceDetail ---

export async function getInvoiceDetail(
  id: number,
  include: string[] = ["grn_mapping", "exceptions", "approval_logs", "revision_requests", "parse_result", "line_items", "validation_result", "credit_notes", "match_result"]
): Promise<V2InvoiceDetailResponse> {
  const params = include.length ? `?include=${include.join(",")}` : "";
  return api.get(`${INVOICE_BASE_URL}/${id}${params}`);
}

export function invoiceDetailQuery(id: number) {
  return {
    queryKey: invoiceKeys.detail(id),
    queryFn: () => getInvoiceDetail(id),
    enabled: !!id,
  };
}

export function useInvoiceDetail(id: number) {
  return useQuery(invoiceDetailQuery(id));
}

// --- getPODetails (v1 API) ---

const V1_BASE_URL = "/fulfilment/accounts-payable/api/v1/vip";

export type GrnEntry = {
  grn_id: number;
  allocated_value: number;
};

export type InvoiceEntry = {
  invoice_no?: string;
  invoice_value?: number | null;
  /** Cash paid against this invoice via UTR (legacy + v2 union, ENG-27224). */
  amount_paid?: number | null;
  /** Primary UTR behind amount_paid (first settled line). */
  utr?: string | null;
  /** Every UTR behind amount_paid; omitted when none. */
  utrs?: string[];
  /** PO advance this invoice consumed. */
  advance_adjusted?: number | null;
  /**
   * Held credit (open advance) set off against this invoice (ENG-27825);
   * amount_paid + advance_adjusted + open_advance_adjusted settles it.
   */
  open_advance_adjusted?: number | null;
  grns?: GrnEntry[];
};

/**
 * One disbursement line on the PO. Since ENG-27224 this is the union of legacy
 * CSV rows (payment_type "Invoice"/"Advance") and v2 UTR lines ("ADVANCE" /
 * "RUNNING" / "FINAL"). Advance lines carry no invoice_no. Rows have no stable
 * id — never key on it.
 */
export type PoPaymentEntry = {
  payment_type?: string;
  invoice_no?: string | null;
  po_code?: string;
  utr?: string | null;
  amount?: number;
  payment_date?: string | null;
};

export type PODetailsItem = {
  po_code: string;
  order_value: number;
  order_status: string;
  invoice_status: string;
  vendor_name: string;
  vendor_facility_id: number;
  source: string;
  milestone: string;
  customer_name: string;
  project_id: string;
  advance_required: boolean;
  invoices: InvoiceEntry[];
  payments?: PoPaymentEntry[] | null;
  /** Sum of every line in `payments[]` (legacy + v2 union). */
  total_amount_paid?: number;
  metadata?: { po_id?: number | null; [key: string]: unknown };
  created?: string;
  modified?: string;
};

export type PODetailsResponse = {
  po_list: PODetailsItem[];
};

export async function getPODetails(poCode: string): Promise<PODetailsResponse> {
  return api.get(`${V1_BASE_URL}/po-details?po_code=${poCode}&include=grns`);
}

export function usePODetails(poCode: string) {
  return useQuery({
    queryKey: invoiceKeys.poDetails(poCode),
    queryFn: () => getPODetails(poCode),
    enabled: !!poCode,
  });
}

// --- getV1InvoiceDetail (for old invoices without lifecycle_status) ---

export async function getV1InvoiceDetail(id: number): Promise<Record<string, unknown>> {
  return api.get(`${V1_BASE_URL}/invoice/${id}`);
}

export function useV1InvoiceDetail(id: number | null) {
  return useQuery({
    queryKey: invoiceKeys.v1Detail(id ?? 0),
    queryFn: () => getV1InvoiceDetail(id!),
    enabled: !!id,
  });
}

// --- getInvoiceAttachments ---

export function useInvoiceAttachments(invoiceId: number) {
  return useQuery({
    queryKey: invoiceKeys.attachments(invoiceId),
    queryFn: () => getInvoiceAttachments(invoiceId),
    enabled: !!invoiceId,
  });
}

// --- getInvoiceStatus (vendor timeline) ---

export async function getInvoiceStatus(id: number): Promise<InvoiceStatusResponse> {
  return api.get(`${INVOICE_BASE_URL}/${id}/status`);
}

export function invoiceStatusQuery(id: number) {
  return {
    queryKey: [...invoiceKeys.detail(id), "status"] as const,
    queryFn: () => getInvoiceStatus(id),
    enabled: !!id,
  };
}

export function useInvoiceStatus(id: number) {
  return useQuery(invoiceStatusQuery(id));
}

